import {
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'

import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Event,
  MutationUpdateEventArgs,
  PaginatedEvent,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useTribeClient } from '../../useTribeClient.js'

import { getEventKey, getEventRecurrencesKey } from './event.key.js'
import { getQueriesContainingEvent } from './utils.js'

export const useUpdateEvent = (options?: {
  useMutationOptions?: UseMutationOptions<
    Event,
    ClientError,
    MutationUpdateEventArgs,
    { snapshot: [QueryKey, InfiniteData<PaginatedEvent>][] }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<
    Event,
    ClientError,
    MutationUpdateEventArgs,
    { snapshot: [QueryKey, InfiniteData<PaginatedEvent>][] }
  >((input: MutationUpdateEventArgs) => client.events.update(input), {
    ...useMutationOptions,
    onSuccess: (newEvent, variables, context) => {
      queryClient.invalidateQueries(
        getEventKey({ variables: { id: variables.eventId } }),
      )

      queryClient.invalidateQueries(
        getEventRecurrencesKey({
          variables: { recurrenceId: newEvent?.recurrenceId },
        }),
      )

      getQueriesContainingEvent(queryClient, variables.eventId).forEach(query =>
        query.invalidate(),
      )

      useMutationOptions?.onSuccess?.(newEvent, variables, context)
    },
  })
}
